import { useState, useEffect } from 'react';
import { X, Crown, Sparkles, Check } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

// localStorage gate with a 6-hour expiry, so the popup re-appears for returning visitors
// without spamming them on every tab refresh.
const STORAGE_KEY = 'be_founders_popup_v4';
const SUPPRESS_HOURS = 6;
const DELAY_MS = 1800;

const isSuppressed = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const seenAt = parseInt(raw, 10);
    if (!Number.isFinite(seenAt)) return false;
    return Date.now() - seenAt < SUPPRESS_HOURS * 60 * 60 * 1000;
  } catch { return false; }
};

const markSeen = () => {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
};

const FoundersPopup = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Only show on the home page
    if (location.pathname !== '/') return;
    if (isSuppressed()) return;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const close = () => {
    markSeen();
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="founders-popup"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={close}
      />

      {/* card */}
      <div className="relative w-full max-w-[560px] bg-[#0F172A] text-white rounded-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-300">
        {/* aurora glows */}
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[#FF9933]/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -right-20 h-80 w-80 rounded-full bg-[#138808]/25 blur-3xl" />
        <div className="pointer-events-none absolute top-1/3 left-1/2 h-56 w-56 rounded-full bg-[#D4AF37]/25 blur-3xl" />

        {/* dot pattern */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, #D4AF37 0px, transparent 1.2px), radial-gradient(circle at 70% 60%, #ffffff 0px, transparent 1.2px)',
            backgroundSize: '24px 24px, 32px 32px',
          }}
        />

        {/* close */}
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          data-testid="founders-popup-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative p-8 sm:p-10 text-center">
          {/* badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] sm:text-xs uppercase tracking-[0.18em] mb-6">
            <Sparkles className="h-3 w-3" /> Founders' Launch Offer
          </div>

          {/* crown */}
          <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#b8932f] flex items-center justify-center shadow-lg shadow-[#D4AF37]/30 mb-6">
            <Crown className="h-8 w-8 text-[#0F172A]" />
          </div>

          <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl font-bold leading-tight mb-3">
            Premium is <span className="text-[#D4AF37]">free for the first month</span>.
          </h2>
          <p className="text-white/75 text-sm sm:text-base leading-relaxed mb-2">
            First month on us — then continue on a paid plan (₹ chargeable monthly) only if you love it.
          </p>
          <p className="text-white/55 text-xs sm:text-sm mb-7">
            No card. No catch. Activated the moment you sign in.
          </p>

          {/* perks */}
          <ul className="text-left max-w-sm mx-auto space-y-2.5 mb-8 text-sm">
            {[
              'Unlimited verified property listings',
              'Unlimited owner-contact reveals',
              'Priority placement in search results',
              'Concierge support — India + NRI desk',
            ].map((p) => (
              <li key={p} className="flex items-start gap-3 text-white/85">
                <span className="mt-0.5 h-5 w-5 rounded-full bg-[#D4AF37] text-[#0F172A] flex items-center justify-center flex-shrink-0">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                {p}
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              onClick={close}
              className="bg-[#D4AF37] hover:bg-[#b8932f] text-[#0F172A] font-bold px-7 py-3 rounded-xl transition-all hover:scale-[1.02] active:scale-95"
              data-testid="founders-popup-cta"
            >
              Claim My Free Month
            </Link>
            <button
              onClick={close}
              className="text-white/70 hover:text-white text-sm font-medium px-4 py-3 transition-colors"
              data-testid="founders-popup-explore"
            >
              Continue exploring
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoundersPopup;
